import { useTranslation } from 'react-i18next';

export const useStructuredData = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';

  const getOrganizationData = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AlertaLegal",
    "url": siteUrl,
    "logo": `${siteUrl}/logo192.png`,
    "description": currentLang === 'en'
      ? "Legal publication monitoring service for Costa Rica"
      : "Servicio de monitoreo de publicaciones legales para Costa Rica",
    "areaServed": {
      "@type": "Country",
      "name": "Costa Rica"
    },
    "serviceType": currentLang === 'en' 
      ? "Legal Document Monitoring"
      : "Monitoreo de Documentos Legales"
  });

  const getWebSiteData = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AlertaLegal",
    "url": siteUrl,
    "description": currentLang === 'en'
      ? "Monitor legal publications and get alerts when your name appears"
      : "Monitorea publicaciones legales y recibe alertas cuando aparezca tu nombre",
    "inLanguage": currentLang === 'en' ? 'en-US' : 'es-CR',
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  });

  const getWebPageData = (pageTitle: string, pageDescription: string, pagePath: string) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": `${siteUrl}${pagePath}`,
    "inLanguage": currentLang === 'en' ? 'en-US' : 'es-CR',
    "isPartOf": {
      "@type": "WebSite",
      "name": "AlertaLegal",
      "url": siteUrl
    }
  });

  const getLegalPageData = (pageTitle: string, pageDescription: string, pagePath: string, legalType: 'PrivacyPolicy' | 'TermsOfService') => ({
    "@context": "https://schema.org",
    "@type": legalType,
    "name": pageTitle,
    "description": pageDescription,
    "url": `${siteUrl}${pagePath}`,
    "inLanguage": currentLang === 'en' ? 'en-US' : 'es-CR',
    "dateModified": "2025-07-14",
    "datePublished": "2025-07-14",
    "publisher": {
      "@type": "Organization",
      "name": "AlertaLegal",
      "url": siteUrl
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "AlertaLegal",
      "url": siteUrl
    },
    "mainEntity": {
      "@type": "Organization",
      "name": "AlertaLegal",
      "url": siteUrl
    }
  });

  const getServiceData = () => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AlertaLegal",
    "provider": {
      "@type": "Organization",
      "name": "AlertaLegal"
    },
    "serviceType": currentLang === 'en' 
      ? "Legal Document Monitoring Service"
      : "Servicio de Monitoreo de Documentos Legales",
    "description": currentLang === 'en'
      ? "Automated monitoring of legal publications in Costa Rica with email notifications"
      : "Monitoreo automático de publicaciones legales en Costa Rica con notificaciones por correo",
    "areaServed": {
      "@type": "Country",
      "name": "Costa Rica"
    },
    "audience": {
      "@type": "Audience",
      "geographicArea": {
        "@type": "Country",
        "name": "Costa Rica"
      }
    }
  });

  const getFAQPageData = (pageTitle: string, pageDescription: string, pagePath: string) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": `${siteUrl}${pagePath}`,
    "inLanguage": currentLang === 'en' ? 'en-US' : 'es-CR',
    "isPartOf": {
      "@type": "WebSite",
      "name": "AlertaLegal",
      "url": siteUrl
    },
    "mainEntity": [
      {
        "@type": "Question",
        "name": currentLang === 'en' ? "What is Alerta Legal?" : "¿Qué es Alerta Legal?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": currentLang === 'en' 
            ? "Alerta Legal is an automated monitoring service that scans legal publications in Costa Rica to identify when your name or national ID number appears in official documents."
            : "Alerta Legal es un servicio de monitoreo automatizado que escanea publicaciones legales en Costa Rica para identificar cuándo tu nombre o número de cédula aparece en documentos oficiales."
        }
      },
      {
        "@type": "Question", 
        "name": currentLang === 'en' ? "Is it free?" : "¿Es gratis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": currentLang === 'en'
            ? "Yes, Alerta Legal is completely free to use. We believe everyone should have access to information about legal matters that may affect them."
            : "Sí, Alerta Legal es completamente gratuito. Creemos que todos deberían tener acceso a información sobre asuntos legales que puedan afectarles."
        }
      }
    ]
  });

  const getEducationalPageData = (pageTitle: string, pageDescription: string, pagePath: string) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": `${siteUrl}${pagePath}`,
    "inLanguage": currentLang === 'en' ? 'en-US' : 'es-CR',
    "about": {
      "@type": "Thing",
      "name": currentLang === 'en' ? "Identity Protection" : "Protección de Identidad"
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "AlertaLegal",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "AlertaLegal",
      "url": siteUrl
    }
  });

  const getTrustPageData = (pageTitle: string, pageDescription: string, pagePath: string) => ({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": `${siteUrl}${pagePath}`,
    "inLanguage": currentLang === 'en' ? 'en-US' : 'es-CR',
    "about": {
      "@type": "Organization",
      "name": "AlertaLegal",
      "url": siteUrl
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "AlertaLegal",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "AlertaLegal",
      "url": siteUrl
    }
  });

  return {
    getOrganizationData,
    getWebSiteData,
    getWebPageData,
    getLegalPageData,
    getServiceData,
    getFAQPageData,
    getEducationalPageData,
    getTrustPageData
  };
};

export default useStructuredData;